// (c) chaehongee. Confidential and proprietary.

import { useMemo, createElement } from 'react'
import { Download, Copy, Link as LinkIcon, Trash2, EyeOff } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks/hooks'
import { removeNode } from '../../../../store/slices/sessionSlice'
import { removeItem as removeCanvasItem } from '../../../../store/slices/canvasSlice'
import { setNodes as setSelectedNodes, setHovered } from '../../../../store/slices/selectionSlice'
import { deleteNode as apiDeleteNode } from '../../../../services/node'
import { fetchableUrl } from './useDownloadUrl'
import type { ContextMenuItem, MenuTarget } from './types'
import { selectActiveSessionId } from '../../../../store/selectors/sessionSelectors'
import { selectSelectedNodeIdsBySessionId } from '../../../../store/selectors/selectionSelectors'

interface UseContextMenuItemsParams {
    target: MenuTarget | null
    downloadUrl: (url: string) => Promise<void>
}

export function useContextMenuItems(params: UseContextMenuItemsParams): ContextMenuItem[] {
    const { target, downloadUrl } = params
    const dispatch = useAppDispatch()
    const activeSessionId = useAppSelector(selectActiveSessionId)
    const selectedIds = useAppSelector((s) => (
        activeSessionId ? selectSelectedNodeIdsBySessionId(s, activeSessionId) : []
    ))
    
    return useMemo<ContextMenuItem[]>(() => {
        if (!target) return []
        if (!activeSessionId) return []
        const items: ContextMenuItem[] = []

        items.push({
            id: 'hide-image',
            label: 'Hide Image',
            icon: createElement(EyeOff),
            onClick: () => {
                try {
                    dispatch(removeCanvasItem({ sessionId: activeSessionId, itemId: target.id }))
                    const next = selectedIds.filter((id) => id !== target.id)
                    dispatch(setSelectedNodes({ sessionId: activeSessionId, nodeIds: next }))
                    dispatch(setHovered({ sessionId: activeSessionId, hoveredNodeId: null }))
                } catch { }
            },
        })

        items.push({
            id: 'save-image',
            label: 'Save Image',
            icon: createElement(Download),
            onClick: () => { void downloadUrl(target.src) },
        })

        items.push({
            id: 'copy-image',
            label: 'Copy Image',
            icon: createElement(Copy),
            onClick: async () => {
                try {
                    // Use proxy for cross-origin images and pass a Promise<Blob>
                    // to ClipboardItem so clipboard.write() runs within the user
                    // gesture (avoids activation-expiry rejection).
                    const ClipboardItemCtor: any = (window as any).ClipboardItem
                    if (navigator.clipboard && ClipboardItemCtor) {
                        const blobPromise = fetch(fetchableUrl(target.src))
                            .then((r) => r.blob())
                            .then((b) => new Blob([b], { type: 'image/png' }))
                        const item = new ClipboardItemCtor({ 'image/png': blobPromise })
                        await navigator.clipboard.write([item])
                    } else {
                        await navigator.clipboard.writeText(target.src)
                    }
                } catch (err) {
                    console.warn('[Canvas] copy image failed; copying URL instead', err)
                    try { await navigator.clipboard.writeText(target.src) } catch { }
                }
            },
        })

        items.push({
            id: 'copy-image-url',
            label: 'Copy Image URL',
            icon: createElement(LinkIcon),
            onClick: async () => {
                try { await navigator.clipboard.writeText(target.src) } catch (err) {
                    console.warn('[Canvas] copy url failed', err)
                }
            },
        })

        items.push({
            id: 'delete',
            label: 'Delete Node',
            icon: createElement(Trash2),
            onClick: async () => {
                const nodeId = target.id
                // Optimistic updates
                try {
                    dispatch(removeNode({ sessionId: activeSessionId, nodeId }))
                    dispatch(removeCanvasItem({ sessionId: activeSessionId, itemId: nodeId }))
                    const next = selectedIds.filter((id) => id !== nodeId)
                    dispatch(setSelectedNodes({ sessionId: activeSessionId, nodeIds: next }))
                    dispatch(setHovered({ sessionId: activeSessionId, hoveredNodeId: null }))
                } catch { }
                // Backend delete (best-effort)
                try {
                    await apiDeleteNode(nodeId)
                } catch (err) {
                    console.warn('[Canvas] delete node failed', err)
                }
            },
        })
        
        return items
    }, [target, downloadUrl, dispatch, selectedIds, activeSessionId])
}

export default useContextMenuItems

